import React from 'react';
import styled from 'styled-components';
import { Map as ReactMap, Marker } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import Card from 'components/Card';
import CardHeader from 'components/CardHeader';

import Spinner from 'components/Spinner';
import history from '../../../history';
import { useDistributionStore } from 'apiTyped/hooks/distribution.query';
import { getColorToStatus } from 'utils/pos';

const MAPBOX_TOKEN =
  'pk.eyJ1IjoiZGMtYW50b2luZSIsImEiOiJjanM1cXNpNzAwZ3ZoM3lzYjZvNjQ3dTJoIn0.BVKVyGXmYOqgvZqDWcMKXA';

const StoreContainer = styled.div`
  width: 100%;
  padding: 20px;

  .back-button {
    margin-bottom: 20px;
  }

  .content-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;

    @media (max-width: 1200px) {
      grid-template-columns: 1fr;
    }
  }

  .store-info {
    padding: 30px;
    font-family: ${(props) => props.theme.fonts.sfcompact_text};

    .store-name {
      font-size: 24px;
      font-weight: 600;
      margin-bottom: 10px;
      color: #2b3d54;
    }

    .store-type {
      font-size: 14px;
      color: #6c757d;
      margin-bottom: 20px;
      text-transform: capitalize;
    }

    .info-section {
      margin-bottom: 25px;

      .section-title {
        font-size: 14px;
        font-weight: 600;
        color: #2b3d54;
        text-transform: uppercase;
        margin-bottom: 10px;
        border-bottom: 1px solid #e8e9eb;
        padding-bottom: 5px;
      }

      .info-row {
        display: flex;
        padding: 8px 0;
        border-bottom: 1px solid #f5f5f5;

        &:last-child {
          border-bottom: none;
        }

        .label {
          font-weight: 600;
          color: #153749;
          min-width: 180px;
          font-size: 13px;
        }

        .value {
          color: #153749;
          font-size: 13px;
          word-break: break-word;

          a {
            color: ${(props) => props.theme.colors['terra-cotta']};
            text-decoration: none;

            &:hover {
              text-decoration: underline;
            }
          }
        }
      }
    }

    .badge {
      display: inline-block;
      padding: 4px 12px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;
      margin-right: 8px;
      margin-bottom: 8px;

      &.positive {
        background-color: #d4edda;
        color: #155724;
      }

      &.negative {
        background-color: #f8d7da;
        color: #721c24;
      }

      &.neutral {
        background-color: #d1ecf1;
        color: #0c5460;
      }

      &.new {
        background-color: #fff3cd;
        color: #856404;
      }
    }
  }

  .map-container {
    height: 400px;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const MarkerContainer = styled.div<{ network_code: string; state: string }>`
  width: 20px;
  height: 20px;
  border-radius: ${(props) => (props.network_code === 'ON' ? '50%' : '0px')};
  background-color: ${(props) => getColorToStatus(props.state)};
  border: 2px solid white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

type StoreProps = {
  match: {
    params: {
      storeId: string;
    };
  };
};

const Store: React.FC<StoreProps> = (props) => {
  const { storeId } = props.match.params;
  const { isLoading, data: storeData } = useDistributionStore(storeId);

  const handleBackClick = (): void => {
    history.push('/distribution/restaurants/points-of-sale');
  };

  if (isLoading || !storeData) {
    return <Spinner loading={true} />;
  }

  const hasLocation = storeData!.latitude && storeData!.longitude;

  const getStateBadgeClass = (state: string) => {
    switch (state) {
      case 'pos':
        return 'positive';
      case 'lost':
        return 'negative';
      case 'todo':
        return 'neutral';
      default:
        return '';
    }
  };

  const getNetworkLabel = (code: string) => {
    return code === 'ON' ? 'Restaurant' : 'Wine Shop';
  };

  return (
    <StoreContainer>
      <Card>
        <CardHeader title="Store Details" fontSize="20px" />
        <div className="store-info">
          <div className="store-name">{storeData.name}</div>
          <div className="store-type">
            {getNetworkLabel(storeData.network_code)}
            {storeData.store_type && ` • ${storeData.store_type}`}
          </div>

          <div>
            <span className={`badge ${getStateBadgeClass(storeData.state)}`}>
              {storeData.state_translated || storeData.state}
            </span>
            {storeData.is_new_store && (
              <span className="badge new">New Store</span>
            )}
            {storeData.is_flow_store && (
              <span className="badge">Flow Store</span>
            )}
          </div>

          {/* Basic Information */}
          <div className="info-section">
            <div className="section-title">Contact Information</div>
            <div className="info-row">
              <div className="label">Address:</div>
              <div className="value">{storeData.address}</div>
            </div>
            {storeData.postal_code && (
              <div className="info-row">
                <div className="label">Postal Code:</div>
                <div className="value">{storeData.postal_code}</div>
              </div>
            )}
            {storeData.city && (
              <div className="info-row">
                <div className="label">City:</div>
                <div className="value">{storeData.city}</div>
              </div>
            )}
            {storeData.region && (
              <div className="info-row">
                <div className="label">Region:</div>
                <div className="value">{storeData.region}</div>
              </div>
            )}
            {storeData.country && (
              <div className="info-row">
                <div className="label">Country:</div>
                <div className="value">{storeData.country}</div>
              </div>
            )}
            <div className="info-row">
              <div className="label">Phone:</div>
              <div className="value">
                {storeData.phone ? (
                  <a href={`tel:${storeData.phone}`}>{storeData.phone}</a>
                ) : (
                  '-'
                )}
              </div>
            </div>
            <div className="info-row">
              <div className="label">Email:</div>
              <div className="value">
                {storeData.email ? (
                  <a href={`mailto:${storeData.email}`}>{storeData.email}</a>
                ) : (
                  '-'
                )}
              </div>
            </div>
            <div className="info-row">
              <div className="label">Website:</div>
              <div className="value">
                {storeData.site ? (
                  <a
                    href={storeData.site}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {storeData.site}
                  </a>
                ) : (
                  '-'
                )}
              </div>
            </div>
          </div>

          {/* Quality & Recognition */}
          {(storeData.michelin_stars ||
            storeData.michelin_green_star ||
            storeData.ws_award ||
            storeData.world_best ||
            storeData.wine_service_rating) && (
            <div className="info-section">
              <div className="section-title">Quality & Recognition</div>
              {storeData.michelin_stars && (
                <div className="info-row">
                  <div className="label">Michelin Stars:</div>
                  <div className="value">
                    {'⭐'.repeat(storeData.michelin_stars)}
                  </div>
                </div>
              )}
              {storeData.michelin_green_star && (
                <div className="info-row">
                  <div className="label">Michelin Green Star:</div>
                  <div className="value">Yes</div>
                </div>
              )}
              {storeData.ws_award && (
                <div className="info-row">
                  <div className="label">WS Award:</div>
                  <div className="value">{storeData.ws_award}</div>
                </div>
              )}
              {storeData.world_best && (
                <div className="info-row">
                  <div className="label">World Best:</div>
                  <div className="value">{storeData.world_best}</div>
                </div>
              )}
              {storeData.wine_service_rating && (
                <div className="info-row">
                  <div className="label">Wine Service Rating:</div>
                  <div className="value">{storeData.wine_service_rating}</div>
                </div>
              )}
            </div>
          )}

          {/* Distribution Numbers */}
          <div className="info-section">
            <div className="section-title">Distribution Numbers (DN)</div>
            <div className="info-row">
              <div className="label">Total DN:</div>
              <div className="value">{storeData.dn_total}</div>
            </div>
            {storeData.dn_spirits > 0 && (
              <div className="info-row">
                <div className="label">Spirits:</div>
                <div className="value">{storeData.dn_spirits}</div>
              </div>
            )}
            {storeData.dn_bdx > 0 && (
              <div className="info-row">
                <div className="label">Bordeaux:</div>
                <div className="value">{storeData.dn_bdx}</div>
              </div>
            )}
            {storeData.dn_champ > 0 && (
              <div className="info-row">
                <div className="label">Champagne:</div>
                <div className="value">{storeData.dn_champ}</div>
              </div>
            )}
            {storeData.dn_italy > 0 && (
              <div className="info-row">
                <div className="label">Italy:</div>
                <div className="value">{storeData.dn_italy}</div>
              </div>
            )}
            {storeData.dn_napa > 0 && (
              <div className="info-row">
                <div className="label">Napa:</div>
                <div className="value">{storeData.dn_napa}</div>
              </div>
            )}
            {storeData.dn_whisky > 0 && (
              <div className="info-row">
                <div className="label">Whisky:</div>
                <div className="value">{storeData.dn_whisky}</div>
              </div>
            )}
            {storeData.dn_rum > 0 && (
              <div className="info-row">
                <div className="label">Rum:</div>
                <div className="value">{storeData.dn_rum}</div>
              </div>
            )}
            {storeData.dn_brandy > 0 && (
              <div className="info-row">
                <div className="label">Brandy:</div>
                <div className="value">{storeData.dn_brandy}</div>
              </div>
            )}
            {storeData.dn_gin > 0 && (
              <div className="info-row">
                <div className="label">Gin:</div>
                <div className="value">{storeData.dn_gin}</div>
              </div>
            )}
            {storeData.dn_vodka > 0 && (
              <div className="info-row">
                <div className="label">Vodka:</div>
                <div className="value">{storeData.dn_vodka}</div>
              </div>
            )}
            {storeData.dn_tequila > 0 && (
              <div className="info-row">
                <div className="label">Tequila:</div>
                <div className="value">{storeData.dn_tequila}</div>
              </div>
            )}
          </div>

          {/* Pricing */}
          {(storeData.avg_all_price ||
            storeData.median_all_price ||
            storeData.median_btg_price) && (
            <div className="info-section">
              <div className="section-title">Pricing</div>
              {storeData.avg_all_price && (
                <div className="info-row">
                  <div className="label">Average Price:</div>
                  <div className="value">
                    {storeData.avg_all_price.toLocaleString('fr-FR', {
                      style: 'currency',
                      currency: storeData.displayed_currency || 'EUR'
                    })}
                  </div>
                </div>
              )}
              {storeData.median_all_price && (
                <div className="info-row">
                  <div className="label">Median Price:</div>
                  <div className="value">
                    {storeData.median_all_price.toLocaleString('fr-FR', {
                      style: 'currency',
                      currency: storeData.displayed_currency || 'EUR'
                    })}
                  </div>
                </div>
              )}
              {storeData.median_btg_price && (
                <div className="info-row">
                  <div className="label">Median BTG Price:</div>
                  <div className="value">
                    {storeData.median_btg_price.toLocaleString('fr-FR', {
                      style: 'currency',
                      currency: storeData.displayed_currency || 'EUR'
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Additional Information */}
          {(storeData.hotel || storeData.group) && (
            <div className="info-section">
              <div className="section-title">Additional Information</div>
              {storeData.hotel && (
                <div className="info-row">
                  <div className="label">Hotel:</div>
                  <div className="value">{storeData.hotel}</div>
                </div>
              )}
              {storeData.group && (
                <div className="info-row">
                  <div className="label">Group:</div>
                  <div className="value">{storeData.group}</div>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Map */}
      {hasLocation && (
        <Card>
          <CardHeader title="Location" fontSize="20px" />
          <div className="map-container">
            <ReactMap
              mapboxAccessToken={MAPBOX_TOKEN}
              initialViewState={{
                latitude: storeData.latitude!,
                longitude: storeData.longitude!,
                zoom: 14
              }}
              style={{ width: '100%', height: '100%' }}
              mapStyle="mapbox://styles/mapbox/streets-v11"
              dragPan={false}
              scrollZoom={false}
              doubleClickZoom={false}
              touchZoomRotate={false}
              dragRotate={false}
            >
              <Marker
                latitude={storeData.latitude!}
                longitude={storeData.longitude!}
                anchor="center"
              >
                <MarkerContainer
                  network_code={storeData.network_code}
                  state={storeData.state}
                />
              </Marker>
            </ReactMap>
          </div>
        </Card>
      )}
    </StoreContainer>
  );
};

export default Store;
